import { AccountService } from './shared/services/account.service';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { setDirectionScheme } from './shared/set-direction-scheme';
import { Title } from '@angular/platform-browser';
import cssVars from 'css-vars-ponyfill';
import { SocketService } from './shared/services/socket.service';
import { AuthService } from './shared/services/auth.service';
import * as Sentry from '@sentry/browser';
import { TokenStorageService } from './shared/services/helpers/token.service';
import { SocketData } from './shared/interfaces/socket-data.interface';
import { Account } from './shared/interfaces/profile.interface';
import { setModeTheme } from './shared/set-color-scheme';
import { CookieService } from './shared/services/helpers/cookie.service';
import { ConfigService } from './shared/services/config.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  favIcon: HTMLLinkElement = document.querySelector('#appIcon');
  @HostListener('window:resize')
  onResize() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }
  @ViewChild('modeWrapper') modeWrapper: ElementRef;
  account: Account;
  private hiddenAt: number | null = null;
  private visibilityHandler: () => void;
  constructor(
    public translate: TranslateService,
    public accountService: AccountService,
    private titleService: Title,
    private socketService: SocketService,
    private authService: AuthService,
    private tokenStorage: TokenStorageService,
    private renderer: Renderer2,
    private cookieService: CookieService,
    private configService: ConfigService,
    private router: Router
  ) {
    // set language

    const account = this.accountService.getAccount();
    this.account = account;
    translate.addLangs(environment.languages);
    translate.setDefaultLang(environment.defaultLang);
    account && environment.languages.includes(account.language)
      ? translate.use(account.language)
      : translate.use('en');

    // set color scheme
    setDirectionScheme(this.translate.currentLang);

    // set vh (need for mobile devices)
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // set title and favicon
    this.setTitle(environment.title);
    this.changeIcon();

    // refresh token
    const token = this.tokenStorage.getToken();
    if (token) {
      this.authService.startRefreshTokenTimer();
    }
  }

  async ngOnInit() {
    await this.configService.loadConfig();
    const sentryUrl = this.configService.getSentryUrl();
    if (sentryUrl) {
      Sentry.init({
        dsn: sentryUrl,
      });
    }

    cssVars();
    if (this.account) {
      this.socketService.getEventData().subscribe((res: SocketData) => {
        if (res.event === this.socketService.EVENTS.logoutUser) {
          this.socketService.unSubscribe(this.account.id);
          this.authService.clearStorage();
          this.router.navigate(['/auth']);
        }
      });
    }
    // handle away duration
    this.visibilityHandler = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now();
    } else if (document.visibilityState === 'visible' && this.hiddenAt) {
      const now = Date.now();
      const awayDuration = now - this.hiddenAt;
      const thirtyMinutes = 30 * 60 * 1000;
      if (awayDuration >= thirtyMinutes) {
        this.authService.refreshToken().subscribe(() => {
          setTimeout(() => {
            location.reload();
          }, 1000);
        });
      }

      this.hiddenAt = null;
    }
  }

  ngAfterViewInit() {
    // set mode
    if (this.account) {
      if (this.account.uses_dark_mode) {
        this.renderer.addClass(document.body, 'dark-mode');
        this.renderer.setAttribute(document.body, 'id', 'mode-wrapper');
        this.cookieService.setCookie('dark-mode', 'true');
      } else {
        this.renderer.removeAttribute(document.body, 'id');
        this.renderer.removeClass(document.body, 'dark-mode');
        this.cookieService.setCookie('dark-mode', 'false');
      }
      setModeTheme(this.account.role, this.account.uses_dark_mode);
    } else {
      const darkMode = this.cookieService.getCookie('dark-mode');
      if (darkMode) {
        this.renderer.addClass(document.body, 'dark-mode');
        this.renderer.setAttribute(document.body, 'id', 'mode-wrapper');
        setModeTheme('participant', true);
      }
    }
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  changeIcon() {
    this.favIcon.href = environment.favicon;
  }
  ngOnDestroy() {
    Sentry.init({
      enabled: false,
    });
    Sentry.close();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }
}
